export const getErrorMessage = (error: any): string => {
    if (error?.response?.data?.message) {
        return error.response.data.message;
    }
    if (error?.response?.data?.error?.message) {
        return error.response.data.error.message;
    }
    if (error?.message) {
        return error.message;
    }
    if (error?.response?.statusText) {
        return error.response.statusText;
    }
    return 'An error occurred.';
};

/**
 * HTTP status behind a failed request. The kufer/sammeln axios clients normalize rejections to
 * `{ message, status }`, while other paths reject with a raw AxiosError, so check both shapes.
 */
export const getStatusCode = (error: unknown): number | undefined => {
    if (typeof error === 'object' && error !== null) {
        const maybeAxios = error as { response?: { status?: number }; status?: number };
        return maybeAxios.response?.status ?? maybeAxios.status;
    }
    return undefined;
};

/** API error code (`{code}` from xver-api and kufer) carried by a rejected request, if any. */
export const getErrorCode = (error: unknown): string | undefined =>
    typeof error === 'object' &&
    error !== null &&
    typeof (error as { code?: unknown }).code === 'string'
        ? (error as { code: string }).code
        : undefined;

/**
 * Whether a kufer/sammeln 401 response body means the session token was rejected: kufer's
 * `unauthenticated`, or the gateway's plain-text reply for a missing, invalid or expired JWT.
 * `github_token_invalid` is a dead GitHub token, not a session problem.
 */
export const isSessionRejected = (status: number, body: unknown): boolean =>
    status === 401 && (getErrorCode(body) === 'unauthenticated' || typeof body === 'string');

/**
 * Whether a rejected kufer GitHub request needs the user to reconnect GitHub. A codeless 401 comes
 * from a kufer older than `github_token_invalid`.
 */
export const isGitHubTokenInvalid = (error: unknown): boolean => {
    const code = getErrorCode(error);
    return code === 'github_token_invalid' || (getStatusCode(error) === 401 && code === undefined);
};
